import { Tag } from "antd";
import type { CSSProperties } from "react";

export type TagStatus =
	| "success"
	| "processing"
	| "error"
	| "warning"
	| "critical"
	| "default";

/**
 * Non-alert classification/tier colors — for Tag chips that label a kind or tier
 * (e.g. "Production" vs "Non-production", "Autonomous" vs "Supervised"), not a live
 * status. Deliberately excludes red/orange/gold: those are reserved for TagStatus's
 * warning/error/critical semantics.
 */
export type ClassificationColor =
	| "neutral"
	| "brand"
	| "brandStrong"
	| "accent";

export const CLASSIFICATION_COLORS: Record<ClassificationColor, string> = {
	neutral: "default",
	brand: "blue",
	brandStrong: "geekblue",
	accent: "gold",
};

type StatusPillProps = {
	status?: TagStatus;
	/** When true, the status dot animates with a repeating pulse. */
	pulse?: boolean;
	children?: React.ReactNode;
};

type StatusPillColors = {
	/** Pill fill (a light tint of the status color). */
	background: string;
	/** 1px border; omitted for the solid Critical fill. */
	border?: string;
	text: string;
	dot: string;
};

/**
 * Pill colors per status, from the brand kit's status sheet. Dots use the antd status
 * tokens (colorSuccess #52ca16, colorPrimary #1864ff, colorWarning #f7bf4f, colorError
 * #f73e41); fills and borders are fixed tints of them. Warning text is a darker amber
 * because #f7bf4f is unreadable on its own tint.
 */
const STATUS_PILL_COLORS: Record<TagStatus, StatusPillColors> = {
	success: {
		background: "#f1fbe9",
		border: "#c6eeae",
		text: "#52ca16",
		dot: "#52ca16",
	},
	processing: {
		background: "#edf3ff",
		border: "#b9cfff",
		text: "#1864ff",
		dot: "#1864ff",
	},
	warning: {
		background: "#fef8ec",
		border: "#fce6b9",
		text: "#d9981b",
		dot: "#f7bf4f",
	},
	error: {
		background: "#feeeee",
		border: "#fcc2c3",
		text: "#f73e41",
		dot: "#f73e41",
	},
	// Critical fills solid: two shades of red are indistinguishable at a glance in a
	// table, and this tier is the one that must never be skimmed past.
	critical: { background: "#d12f32", text: "#ffffff", dot: "#ffffff" },
	default: {
		background: "#f5f6f8",
		border: "#dce6f3",
		text: "#5b6782",
		dot: "#b4bccb",
	},
};

/** A small labeled pill with a colored dot indicating a status. */
export function StatusPill({
	status = "default",
	pulse = false,
	children,
}: StatusPillProps) {
	const colors = STATUS_PILL_COLORS[status];

	return (
		<Tag
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 6,
				padding: "2px 9px",
				borderRadius: 6,
				fontSize: 12,
				lineHeight: "18px",
				background: colors.background,
				border: `1px solid ${colors.border ?? colors.background}`,
				color: colors.text,
			}}
		>
			<span
				aria-hidden
				className={pulse ? "sn-status-dot-pulse" : undefined}
				style={
					{
						width: 6,
						height: 6,
						borderRadius: "50%",
						flex: "none",
						background: colors.dot,
						"--sn-status-dot": colors.dot,
					} as CSSProperties
				}
			/>
			{children}
		</Tag>
	);
}

/**
 * Defines a status badge from a colorMap + labelMap in one line, e.g.
 * `export const AgentHealthBadge = createStatusBadge(AgentHealthColorMap, AgentHealthLabelMap, { pulseWhen: ["PROVISIONING"] });`
 * The returned component always takes a `status` prop, whatever the source enum's own
 * field is called on the domain type — callers rename at the JSX call site, not here.
 */
export function createStatusBadge<K extends string>(
	colorMap: Record<K, TagStatus>,
	labelMap: Record<K, string>,
	// NoInfer: without it, a literal like `["PROVISIONING"]` narrows K to that one
	// member instead of the full enum inferred from colorMap/labelMap.
	options?: { pulseWhen?: NoInfer<K>[] },
) {
	return function StatusBadge({ status }: { status: K }) {
		return (
			<StatusPill
				status={colorMap[status]}
				pulse={options?.pulseWhen?.includes(status)}
			>
				{labelMap[status]}
			</StatusPill>
		);
	};
}

/**
 * Defines a plain-text label from a labelMap in one line: `createLabelTag(XLabelMap)`. Attributes
 * (type, provider, kind) render as text; tags are reserved for state, so the two never look alike.
 */
export function createLabelTag<K extends string>(labelMap: Record<K, string>) {
	return function LabelTag({ type }: { type: K }) {
		return <>{labelMap[type]}</>;
	};
}
